
import React from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { MapPin, Camera } from 'lucide-react';
import { UseFormReturn } from 'react-hook-form';
import { z } from 'zod';
import { formSchema, ceremonyTypes, serviceTypes } from '@/utils/contactFormUtils';
import WeddingDatePicker from './WeddingDatePicker';

interface WeddingDetailsFieldsProps {
  form: UseFormReturn<z.infer<typeof formSchema>>;
}

const WeddingDetailsFields: React.FC<WeddingDetailsFieldsProps> = ({ form }) => {
  const isMobile = useIsMobile();

  return (
    <div className={`space-y-3 ${isMobile ? 'p-4' : 'p-6'} bg-white rounded-lg border border-elegant-200 backdrop-blur-sm shadow-lg`}>
      <h3 className="text-xl font-serif text-elegant-900 pb-2 border-b border-elegant-200 text-left">Szczegóły wydarzenia</h3>
      
      <FormField
        control={form.control}
        name="ceremony_type"
        render={({ field }) => (
          <FormItem className="space-y-1">
            <FormLabel className={`text-${isMobile ? 'xs' : 'sm'} font-inter text-left block text-elegant-700 tracking-wider font-light uppercase`}>RODZAJ CEREMONII</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className={`w-full ${isMobile ? 'py-1.5 text-sm' : 'py-2'} rounded bg-white border-elegant-200 text-elegant-900 focus:ring-0 focus:border-elegant-300 shadow-inner shadow-elegant-100/20`}>
                  <SelectValue placeholder="Wybierz rodzaj ceremonii" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="bg-white border-elegant-200 text-elegant-900">
                {ceremonyTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value} className="focus:bg-elegant-100 focus:text-elegant-900">
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage className="text-red-500 text-xs" />
          </FormItem>
        )}
      />
      
      <WeddingDatePicker form={form} />
      
      <FormField
        control={form.control}
        name="wedding_location"
        render={({ field }) => (
          <FormItem className="space-y-1">
            <FormLabel className={`text-${isMobile ? 'xs' : 'sm'} font-inter text-left block text-elegant-700 tracking-wider font-light uppercase`}>MIEJSCE PRZYJĘCIA</FormLabel>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-elegant-400">
                <MapPin className="h-4 w-4" />
              </div>
              <FormControl>
                <Input
                  placeholder="Nazwa i lokalizacja miejsca wesela"
                  {...field}
                  className={`pl-10 ${isMobile ? 'py-1.5 text-sm' : 'py-2'} rounded bg-white border-elegant-200 text-elegant-900 placeholder:text-elegant-400 focus:ring-0 focus:border-elegant-300 focus:ring-elegant-300 shadow-inner shadow-elegant-100/20`}
                />
              </FormControl>
            </div>
            <FormMessage className="text-red-500 text-xs" />
          </FormItem>
        )}
      />
      
      <FormField
        control={form.control}
        name="service_type"
        render={({ field }) => (
          <FormItem className="space-y-1">
            <FormLabel className={`text-${isMobile ? 'xs' : 'sm'} font-inter text-left block text-elegant-700 tracking-wider font-light uppercase`}>INTERESUJE MNIE</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className={`w-full ${isMobile ? 'py-1.5 text-sm' : 'py-2'} rounded bg-white border-elegant-200 text-elegant-900 focus:ring-0 focus:border-elegant-300 shadow-inner shadow-elegant-100/20`}>
                  <div className="flex items-center">
                    <Camera className="mr-2 h-4 w-4 opacity-70" />
                    <SelectValue placeholder="Wybierz rodzaj usługi" />
                  </div>
                </SelectTrigger>
              </FormControl>
              <SelectContent className="bg-white border-elegant-200 text-elegant-900">
                {serviceTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value} className="focus:bg-elegant-100 focus:text-elegant-900">
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage className="text-red-500 text-xs" />
          </FormItem>
        )}
      />
      
      <FormField
        control={form.control}
        name="gdpr_consent"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start space-x-3 mt-4">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
                className="data-[state=checked]:bg-elegant-300 data-[state=checked]:border-elegant-300 mt-1"
              />
            </FormControl>
            <div className="space-y-1 text-left">
              <FormLabel className={`text-elegant-600 font-normal ${isMobile ? 'text-xs' : 'text-xs'}`}>
                Wyrażam zgodę na przetwarzanie moich danych osobowych zgodnie z polityką RODO w celu odpowiedzi na zapytanie.
              </FormLabel>
              <FormMessage className="text-red-500 text-xs" />
            </div>
          </FormItem>
        )}
      />
    </div>
  );
};

export default WeddingDetailsFields;
